import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, TrendingUp, TrendingDown, Package } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts';
import KPICard from '../components/KPICard';
import api from '../utils/api';
import { formatCurrency } from '../utils/format';

const PERIODS = [
  { label: 'Últimos 7 dias', days: 7 },
  { label: 'Últimos 30 dias', days: 30 },
  { label: 'Este mês', days: 0 },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-xl shadow-md border border-slate-100 p-3 text-xs">
      <p className="font-bold text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: {formatCurrency(p.value)}</p>
      ))}
    </div>
  );
};

export default function FluxoCaixa() {
  const [tab, setTab] = useState('mensal');
  const [activePeriod, setActivePeriod] = useState(1);
  const [fluxoMensal, setFluxoMensal] = useState([]);
  const [fluxoDiario, setFluxoDiario] = useState([]);
  const [indicadores, setIndicadores] = useState(null);
  const [loading, setLoading] = useState(true);

  const getPeriodDates = (idx) => {
    const now = new Date();
    const fim = now.toISOString().split('T')[0];
    if (idx === 2) {
      const inicio = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
      return { data_inicio: inicio, data_fim: fim };
    }
    const days = PERIODS[idx].days;
    const start = new Date(now);
    start.setDate(now.getDate() - days);
    return { data_inicio: start.toISOString().split('T')[0], data_fim: fim };
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data_inicio, data_fim } = getPeriodDates(activePeriod);
      const params = `data_inicio=${data_inicio}&data_fim=${data_fim}`;
      const [mensal, diario, ind] = await Promise.all([
        api.get('/dashboard/fluxo-caixa'),
        api.get(`/dashboard/fluxo-caixa-diario?${params}`),
        api.get(`/dashboard/indicadores?${params}`),
      ]);
      setFluxoMensal(mensal.data);
      setFluxoDiario(diario.data);
      setIndicadores(ind.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [activePeriod]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const ind = indicadores || {};
  const saldo = (ind.faturamento_bruto || 0) - (ind.total_custos || 0) - (ind.total_despesas || 0);

  const chartData = tab === 'mensal' ? fluxoMensal : fluxoDiario;
  const xKey = tab === 'mensal' ? 'mes' : 'dia';

  return (
    <div className="space-y-6 page-enter" data-testid="fluxo-caixa-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Fluxo de Caixa
          </h1>
          <p className="text-slate-500 text-sm mt-1">Visão de entradas e saídas ao longo do tempo</p>
        </div>
        {/* Period pills */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="period-selector">
          {PERIODS.map((p, i) => (
            <button
              key={i}
              onClick={() => setActivePeriod(i)}
              data-testid={`period-${i}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                activePeriod === i
                  ? 'bg-[#0147a0] text-white shadow-sm'
                  : 'bg-white border border-slate-200 text-slate-600 hover:border-[#0147a0]'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="fluxo-kpi-cards">
        <KPICard label="Total Receitas" value={ind.faturamento_bruto} icon={TrendingUp}
          accentColor="#339933" iconBg="#DCFCE7" iconColor="#339933" loading={loading} />
        <KPICard label="Total Custos" value={ind.total_custos} icon={Package}
          accentColor="#f9c900" iconBg="#FFFBEB" iconColor="#c99a1a" loading={loading} />
        <KPICard label="Total Despesas" value={ind.total_despesas} icon={TrendingDown}
          accentColor="#d72d21" iconBg="#FEE2E2" iconColor="#d72d21" loading={loading} />
        <KPICard label="Saldo" value={saldo} icon={DollarSign}
          accentColor={saldo >= 0 ? '#339933' : '#d72d21'}
          iconBg={saldo >= 0 ? '#DCFCE7' : '#FEE2E2'}
          iconColor={saldo >= 0 ? '#339933' : '#d72d21'}
          loading={loading} />
      </div>

      {/* Valores ainda não efetivados (não entram nos totais acima) */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 px-1 text-xs text-slate-500 dark:text-slate-400" data-testid="fluxo-em-aberto">
        <span>Em aberto — <strong className="text-emerald-600">A receber: {formatCurrency(ind.a_receber || 0)}</strong></span>
        <span><strong className="text-rose-600">A pagar: {formatCurrency(ind.a_pagar || 0)}</strong></span>
        <span className="text-slate-400">Os totais acima contam só o que já foi pago ou recebido.</span>
      </div>

      {/* Chart Card */}
      <div className="fin-card p-6" data-testid="fluxo-chart">
        {/* Tabs */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-bold text-slate-800 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Evolução do Fluxo
          </h2>
          <div className="flex rounded-xl border border-slate-200 overflow-hidden">
            {['mensal', 'diário'].map(t => (
              <button
                key={t}
                onClick={() => setTab(t === 'diário' ? 'diario' : t)}
                data-testid={`tab-${t}`}
                className={`px-4 py-2 text-xs font-semibold transition-colors capitalize ${
                  (tab === 'diario' ? 'diário' : tab) === t
                    ? 'bg-[#0147a0] text-white'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-5 mb-4 flex-wrap">
          {[
            { label: 'Receitas', color: '#339933' },
            { label: 'Custos', color: '#f9c900' },
            { label: 'Despesas', color: '#d72d21' },
          ].map(l => (
            <div key={l.label} className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full" style={{ background: l.color }} />
              <span className="text-xs text-slate-500">{l.label}</span>
            </div>
          ))}
        </div>

        {chartData.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center text-slate-400 text-sm">
            Sem dados para o período selecionado
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: '#94A3B8' }} />
              <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="receitas" name="Receitas" stroke="#339933" strokeWidth={2} dot={{ r: 4, fill: '#339933' }} />
              <Line type="monotone" dataKey="custos" name="Custos" stroke="#f9c900" strokeWidth={2} dot={{ r: 4, fill: '#f9c900' }} />
              <Line type="monotone" dataKey="despesas" name="Despesas" stroke="#d72d21" strokeWidth={2} dot={{ r: 4, fill: '#d72d21' }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
